// Agency Commission Hub — 7-tab management center for agency-level commissions
// Tabs: Dashboard | Statements | Ledger | Agent Payouts | Reconciliation | Alerts | Summary
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { csrfHeadersFor } from '../api';
import {
  DollarSign, ArrowLeft, RefreshCw, TrendingUp, TrendingDown,
  AlertTriangle, Users, BarChart3, Upload, FileText, Search,
  CheckCircle, XCircle, Clock, ChevronDown, ChevronRight,
  Download, Mail, Plus, Trash2, Filter, X, Eye, Check,
  AlertCircle, Zap, PieChart,
} from 'lucide-react';

const BASE = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');

// ── Design tokens (dark theme, amber accent) ──────────────────────────────────
const CARD = 'bg-gray-900 border border-gray-800 rounded-xl';
const INPUT = 'bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-amber-400 text-sm w-full';
const SELECT = 'bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-amber-400 text-sm';
const BTN_PRI = 'bg-amber-400 text-gray-900 font-semibold px-4 py-2 rounded-lg hover:bg-amber-300 transition-colors text-sm flex items-center gap-2';
const BTN_SEC = 'border border-gray-600 text-gray-300 px-4 py-2 rounded-lg hover:border-amber-400 hover:text-amber-400 transition-colors text-sm flex items-center gap-2';
const BTN_SM = 'border border-gray-700 text-gray-400 px-3 py-1.5 rounded-lg hover:border-amber-400 hover:text-amber-400 transition-colors text-xs flex items-center gap-1.5';

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const CUR_YEAR = new Date().getFullYear();
const YEARS = Array.from({ length: 5 }, (_, i) => CUR_YEAR - i);

// ── Helpers ───────────────────────────────────────────────────────────────────
function fmtCurrency(n) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(n || 0);
}
function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}
function statusBadge(status) {
  const map = {
    uploaded: 'bg-gray-800 text-gray-400',
    parsing:  'bg-blue-900/50 text-blue-300 animate-pulse',
    parsed:   'bg-amber-900/50 text-amber-300',
    matched:  'bg-purple-900/50 text-purple-300',
    completed:'bg-emerald-900/50 text-emerald-300',
    failed:   'bg-red-900/50 text-red-400',
    pending:  'bg-gray-800 text-gray-400',
    paid:     'bg-emerald-900/50 text-emerald-300',
    voided:   'bg-red-900/50 text-red-400',
    received: 'bg-emerald-900/50 text-emerald-300',
    variance: 'bg-amber-900/50 text-amber-300',
    missing:  'bg-red-900/50 text-red-400',
    disputed: 'bg-orange-900/50 text-orange-300',
    balanced: 'bg-emerald-900/50 text-emerald-300',
    deficit:  'bg-red-900/50 text-red-400',
    surplus:  'bg-blue-900/50 text-blue-300',
  };
  return `text-xs px-2 py-0.5 rounded font-medium ${map[status] || 'bg-gray-800 text-gray-400'}`;
}

// ── Tabs ──────────────────────────────────────────────────────────────────────
const TABS = [
  { id: 'dashboard',    label: 'Dashboard',      icon: BarChart3 },
  { id: 'statements',  label: 'Statements',      icon: FileText },
  { id: 'ledger',      label: 'Ledger',          icon: DollarSign },
  { id: 'payouts',     label: 'Agent Payouts',   icon: Users },
  { id: 'reconcile',   label: 'Reconciliation',  icon: Check },
  { id: 'alerts',      label: 'Alerts',          icon: AlertTriangle },
  { id: 'summary',     label: 'Summary',         icon: PieChart },
];

// ═════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═════════════════════════════════════════════════════════════════════════════
export default function AgencyCommissions() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('dashboard');

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      {/* Header */}
      <div className="bg-gray-900 border-b border-gray-800 px-6 py-4 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/agency/dashboard')} className="p-2 hover:bg-gray-800 rounded-lg text-gray-400 hover:text-white transition-colors">
              <ArrowLeft size={20} />
            </button>
            <div>
              <h1 className="text-xl font-bold text-white flex items-center gap-2">
                <DollarSign className="text-amber-400" size={22} />
                Agency Commission Hub
              </h1>
              <p className="text-xs text-gray-500">Override tracking · Statements · Agent payouts</p>
            </div>
          </div>
        </div>

        {/* Tab bar */}
        <div className="max-w-7xl mx-auto mt-4 flex gap-1 overflow-x-auto">
          {TABS.map(t => {
            const Icon = t.icon;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                  tab === t.id
                    ? 'bg-amber-400 text-gray-900'
                    : 'text-gray-400 hover:text-white hover:bg-gray-800'
                }`}
              >
                <Icon size={14} />
                {t.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto p-6">
        {tab === 'dashboard'   && <DashboardTab />}
        {tab === 'statements'  && <StatementsTab />}
        {tab === 'ledger'      && <LedgerTab />}
        {tab === 'payouts'     && <PayoutsTab />}
        {tab === 'reconcile'   && <ReconciliationTab />}
        {tab === 'alerts'      && <AlertsTab />}
        {tab === 'summary'     && <SummaryTab />}
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// TAB: DASHBOARD
// ═════════════════════════════════════════════════════════════════════════════
function DashboardTab() {
  const [data, setData] = useState(null);
  const [coverage, setCoverage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState(CUR_YEAR);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [dashRes, covRes] = await Promise.all([
        fetch(`${BASE}/agency/commissions/dashboard?year=${year}`, { credentials: 'include' }),
        fetch(`${BASE}/agency/commissions/dashboard/coverage?year=${year}&month=${new Date().getMonth()+1}`, { credentials: 'include' }),
      ]);
      const [dash, cov] = await Promise.all([dashRes.json(), covRes.json()]);
      if (dash.ok) setData(dash);
      if (cov.ok) setCoverage(cov);
    } catch (err) {
      console.error('[AgencyCommissions] dashboard load error:', err);
    } finally {
      setLoading(false);
    }
  }, [year]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <LoadingSpinner />;

  const maxAgent = Math.max(...(data?.agents || []).map(a => a.total), 1);
  const maxMonthly = Math.max(...(data?.monthly || []).map(m => m.total), 1);

  return (
    <div className="space-y-6">
      {/* Year selector */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-white">Commission Overview</h2>
        <div className="flex items-center gap-2">
          <select value={year} onChange={e => setYear(parseInt(e.target.value))} className={SELECT}>
            {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          <button onClick={load} className={BTN_SM}><RefreshCw size={12} />Refresh</button>
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard label="Override Revenue YTD" value={fmtCurrency(data?.overrideRevenueYTD)} color="amber" icon={DollarSign} />
        <KPICard label="Agent Commissions YTD" value={fmtCurrency(data?.agentCommissionsYTD)} color="emerald" icon={TrendingUp} />
        <KPICard label="Revenue at Risk" value={fmtCurrency(data?.revenueAtRisk)} color="red" icon={AlertTriangle} sub="at-risk client estimate" />
        <KPICard label="Agents Active" value={`${data?.activeAgents || 0} / ${data?.totalAgents || 0}`} color="blue" icon={Users} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Monthly bar chart */}
        <div className={`${CARD} p-5 lg:col-span-2`}>
          <h3 className="text-sm font-semibold text-gray-300 mb-4">Override Revenue by Month ({year})</h3>
          <div className="flex items-end gap-1 h-40">
            {(data?.monthly || []).map((m, i) => {
              const pct = maxMonthly > 0 ? (m.total / maxMonthly) * 100 : 0;
              return (
                <div key={i} className="flex-1 flex flex-col items-center gap-1">
                  <div className="relative w-full" style={{ height: '128px' }}>
                    <div
                      className="absolute bottom-0 left-0 right-0 bg-amber-400/80 rounded-t transition-all"
                      style={{ height: `${pct}%` }}
                      title={`${MONTHS[i].slice(0,3)}: ${fmtCurrency(m.total)}`}
                    />
                  </div>
                  <span className="text-xs text-gray-600">{MONTHS[i].slice(0,1)}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Statement coverage */}
        <div className={`${CARD} p-5`}>
          <h3 className="text-sm font-semibold text-gray-300 mb-4">Statement Coverage — This Month</h3>
          {!coverage?.carriers?.length ? (
            <p className="text-sm text-gray-600">No carriers configured</p>
          ) : (
            <div className="space-y-2">
              {coverage.carriers.map(c => (
                <div key={c.carrier} className="flex items-center justify-between">
                  <span className="text-sm text-gray-300 truncate">{c.carrier}</span>
                  {c.received
                    ? <span className="text-xs px-2 py-0.5 rounded bg-emerald-900/50 text-emerald-300 flex items-center gap-1"><CheckCircle size={10} />Received</span>
                    : <span className="text-xs px-2 py-0.5 rounded bg-red-900/30 text-red-400 flex items-center gap-1"><XCircle size={10} />Missing</span>
                  }
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Per-agent leaderboard */}
      <div className={`${CARD} overflow-hidden`}>
        <div className="px-5 py-4 border-b border-gray-800">
          <h3 className="text-sm font-semibold text-gray-300">Agent Leaderboard ({year})</h3>
        </div>
        {!data?.agents?.length ? (
          <div className="p-8 text-center text-gray-600 text-sm">No agent data</div>
        ) : (
          <div className="divide-y divide-gray-800">
            {data.agents.map((a, i) => (
              <div key={a.agentId} className="px-5 py-3">
                <div className="flex items-center justify-between mb-1.5">
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-gray-600 w-5 text-right">#{i+1}</span>
                    <div>
                      <p className="text-sm font-medium text-gray-200">{a.agentName}</p>
                      <p className="text-xs text-gray-600">{a.activeClients} clients</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-bold text-amber-400">{fmtCurrency(a.total)}</p>
                    <div className="flex gap-2 text-xs text-gray-600">
                      <span>FY: {fmtCurrency(a.fyTotal)}</span>
                      <span>RN: {fmtCurrency(a.rnTotal)}</span>
                    </div>
                  </div>
                </div>
                <div className="w-full h-1 bg-gray-800 rounded-full overflow-hidden">
                  <div className="h-full bg-amber-400 rounded-full" style={{ width: `${(a.total / maxAgent) * 100}%` }} />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// TAB: STATEMENTS
// ═════════════════════════════════════════════════════════════════════════════
function StatementsTab() {
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [expandedBatch, setExpandedBatch] = useState(null);
  const [batchDetail, setBatchDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [statementType, setStatementType] = useState('override_only');
  const [matching, setMatching] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [agencyMembers, setAgencyMembers] = useState([]);
  const fileRef = useRef();
  const [dragOver, setDragOver] = useState(false);
  const [showAliases, setShowAliases] = useState(false);
  const [aliases, setAliases] = useState([]);
  const [newAlias, setNewAlias] = useState({ aliasName: '', userId: '', notes: '' });

  const loadAliases = useCallback(async () => {
    try {
      const res = await fetch(`${BASE}/agency/commissions/aliases`, { credentials: 'include' });
      const j = await res.json();
      if (j.ok) setAliases(j.data || []);
    } catch (e) { /* table may not exist yet */ }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [batchRes, membersRes] = await Promise.all([
        fetch(`${BASE}/agency/commissions/statements`, { credentials: 'include' }),
        fetch(`${BASE}/api/agency-setup/team`, { credentials: 'include' }),
      ]);
      const [bj, mj] = await Promise.all([batchRes.json(), membersRes.json()]);
      if (bj.ok) setBatches(bj.data || []);
      if (mj.ok) setAgencyMembers(mj.data || []);
      loadAliases();
    } finally {
      setLoading(false);
    }
  }, [loadAliases]);

  useEffect(() => { load(); }, [load]);

  const loadDetail = async (batchId) => {
    if (expandedBatch === batchId) { setExpandedBatch(null); setBatchDetail(null); return; }
    setExpandedBatch(batchId);
    setLoadingDetail(true);
    try {
      const res = await fetch(`${BASE}/agency/commissions/statements/${batchId}`, { credentials: 'include' });
      const j = await res.json();
      if (j.ok) setBatchDetail({ batchId, lines: j.lines });
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('statementType', statementType);
      const res = await fetch(`${BASE}/agency/commissions/statements/upload`, {
        method: 'POST',
        credentials: 'include',
        headers: { ...csrfHeadersFor('POST') },
        body: fd,
      });
      const j = await res.json();
      if (j.ok) { load(); }
      else { alert(j.error || 'Upload failed'); }
    } finally {
      setUploading(false);
    }
  };

  const handleMatchAgents = async (batchId) => {
    setMatching(true);
    try {
      const res = await fetch(`${BASE}/agency/commissions/statements/${batchId}/match-agents`, {
        method: 'POST', credentials: 'include', headers: { ...csrfHeadersFor('POST') },
      });
      const j = await res.json();
      if (j.ok) {
        load();
        if (expandedBatch === batchId) loadDetail(batchId);
      } else {
        alert(j.error || 'Agent matching failed');
      }
    } finally {
      setMatching(false);
    }
  };

  const handleProcess = async (batchId) => {
    if (!window.confirm('Process matched lines to the override ledger?')) return;
    setProcessing(true);
    try {
      const res = await fetch(`${BASE}/agency/commissions/statements/${batchId}/process`, {
        method: 'POST', credentials: 'include', headers: { ...csrfHeadersFor('POST') },
      });
      const j = await res.json();
      if (j.ok) { load(); alert(`Processed ${j.inserted} entries to ledger.`); }
      else { alert(j.error || 'Processing failed'); }
    } catch (err) {
      alert('Error processing to ledger: ' + err.message);
    } finally {
      setProcessing(false);
    }
  };

  const assignAgent = async (batchId, lineId, agentId) => {
    await fetch(`${BASE}/agency/commissions/statements/${batchId}/lines/${lineId}`, {
      method: 'PUT',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', ...csrfHeadersFor('PUT') },
      body: JSON.stringify({ agentId }),
    });
    loadDetail(batchId);
  };

  const deleteBatch = async (batchId) => {
    if (!window.confirm('Delete this statement and all its lines?')) return;
    await fetch(`${BASE}/agency/commissions/statements/${batchId}`, { method: 'DELETE', credentials: 'include', headers: { ...csrfHeadersFor('DELETE') } });
    load();
  };

  return (
    <div className="space-y-6">
      {/* Upload zone */}
      <div
        className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors cursor-pointer ${
          dragOver ? 'border-amber-400 bg-amber-400/5' : 'border-gray-700 hover:border-gray-600'
        }`}
        onDragOver={e => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={e => { e.preventDefault(); setDragOver(false); const f = e.dataTransfer.files[0]; if (f) handleUpload(f); }}
        onClick={() => fileRef.current?.click()}
      >
        <input ref={fileRef} type="file" className="hidden"
          accept=".pdf,.csv,.xlsx,.xls,.jpg,.jpeg,.png"
          onChange={e => handleUpload(e.target.files[0])}
        />
        {uploading ? (
          <div className="flex flex-col items-center gap-3">
            <RefreshCw className="text-amber-400 animate-spin" size={32} />
            <p className="text-gray-400">Uploading and parsing...</p>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-3">
            <Upload className="text-gray-600" size={32} />
            <p className="text-gray-300 font-medium">Drop carrier statement here or click to browse</p>
            <p className="text-xs text-gray-600">PDF, CSV, XLSX, JPG, PNG · Max 20MB</p>
            <div className="flex items-center gap-3 mt-2" onClick={e => e.stopPropagation()}>
              <select value={statementType} onChange={e => setStatementType(e.target.value)} className={SELECT}>
                <option value="override_only">Override Only</option>
                <option value="full_production">Full Agent Production</option>
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Batch list */}
      {loading ? <LoadingSpinner /> : !batches.length ? (
        <div className={`${CARD} p-12 text-center text-gray-600`}>
          <FileText size={40} className="mx-auto mb-3 opacity-30" />
          <p>No statements uploaded yet</p>
        </div>
      ) : (
        <div className="space-y-3">
          {batches.map(b => (
            <div key={b.id} className={CARD}>
              <div className="p-4 flex items-center justify-between gap-4">
                <div className="flex items-center gap-3 min-w-0">
                  <FileText size={18} className="text-gray-500 flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-200 truncate">{b.original_filename || 'Statement'}</p>
                    <div className="flex items-center gap-3 text-xs text-gray-600 mt-0.5">
                      <span>{b.detected_carrier || 'Unknown carrier'}</span>
                      <span>{fmtDate(b.statement_date)}</span>
                      <span>{b.total_rows || 0} rows</span>
                      <span>{fmtCurrency(b.total_commission_amount)}</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className={statusBadge(b.status)}>{b.status}</span>
                  {(b.status === 'parsed' || b.status === 'matched') && (
                    <button onClick={() => handleMatchAgents(b.id)} disabled={matching} className={BTN_SM}>
                      {matching ? <RefreshCw size={10} className="animate-spin" /> : <Zap size={10} />}
                      Match Agents
                    </button>
                  )}
                  {b.status === 'matched' && b.matched_count > 0 && (
                    <button onClick={() => handleProcess(b.id)} disabled={processing} className={BTN_SM}>
                      <Check size={10} />Process to Ledger
                    </button>
                  )}
                  <button onClick={() => loadDetail(b.id)} className={BTN_SM}>
                    <Eye size={10} />
                    {expandedBatch === b.id ? 'Hide' : 'Lines'}
                  </button>
                  <button onClick={() => deleteBatch(b.id)} className="text-gray-700 hover:text-red-400 transition-colors p-1">
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>

              {/* Expanded lines */}
              {expandedBatch === b.id && (
                <div className="border-t border-gray-800">
                  {loadingDetail ? (
                    <div className="p-6 text-center"><RefreshCw size={20} className="text-amber-400 animate-spin mx-auto" /></div>
                  ) : !batchDetail?.lines?.length ? (
                    <div className="p-6 text-center text-gray-600 text-sm">No line items</div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-gray-800">
                            <th className="text-left text-xs text-gray-500 px-4 py-3 font-medium">Agent Name</th>
                            <th className="text-left text-xs text-gray-500 px-4 py-3 font-medium">Member</th>
                            <th className="text-left text-xs text-gray-500 px-4 py-3 font-medium">Plan</th>
                            <th className="text-left text-xs text-gray-500 px-4 py-3 font-medium">Type</th>
                            <th className="text-right text-xs text-gray-500 px-4 py-3 font-medium">Amount</th>
                            <th className="text-left text-xs text-gray-500 px-4 py-3 font-medium">Agent Match</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-800/50">
                          {[...batchDetail.lines].sort((a, b) => {
                            const order = { unmatched: 0, pending: 1, auto_matched: 2, manual_matched: 3 };
                            return (order[a.match_status] ?? 1) - (order[b.match_status] ?? 1);
                          }).map(line => (
                            <tr key={line.id} className={line.match_status === 'unmatched' || line.match_status === 'pending' ? 'bg-amber-900/10' : ''}>
                              <td className="px-4 py-2.5 text-gray-300">{line.agent_name || '—'}</td>
                              <td className="px-4 py-2.5 text-gray-400 text-xs">{line.member_name || '—'}</td>
                              <td className="px-4 py-2.5 text-gray-400 text-xs">{line.plan_name || line.plan_type || '—'}</td>
                              <td className="px-4 py-2.5">
                                <span className={statusBadge(line.commission_type)}>{line.commission_type || '—'}</span>
                              </td>
                              <td className="px-4 py-2.5 text-right text-gray-300 tabular-nums">
                                {fmtCurrency(line.commission_amount)}
                              </td>
                              <td className="px-4 py-2.5">
                                {line.match_status === 'auto_matched' || line.match_status === 'manual_matched' ? (
                                  <span className="text-xs text-emerald-400 flex items-center gap-1">
                                    <Check size={10} />{line.matched_agent_name || 'Matched'}
                                  </span>
                                ) : (
                                  <div className="flex items-center gap-2">
                                    <select
                                      className="bg-gray-800 border border-amber-700 rounded px-2 py-1 text-xs text-white focus:outline-none focus:ring-1 focus:ring-amber-400"
                                      value={line.matched_agent_id || ''}
                                      onChange={e => assignAgent(b.id, line.id, e.target.value || null)}
                                    >
                                      <option value="">— Assign agent —</option>
                                      {agencyMembers.map(m => (
                                        <option key={m.user_id || m.id} value={m.user_id || m.id}>
                                          {m.first_name} {m.last_name}
                                        </option>
                                      ))}
                                    </select>
                                    {(line.match_status === 'unmatched' || line.match_status === 'pending') && line.member_name && (
                                      <button
                                        className="text-xs text-amber-400 hover:text-amber-300 whitespace-nowrap border border-amber-700 rounded px-1.5 py-0.5"
                                        title={`Create client: ${line.member_name}`}
                                        onClick={() => window.open('/clients/new', '_blank')}
                                      >
                                        <Plus size={10} className="inline mr-0.5"/>Create Client
                                      </button>
                                    )}
                                  </div>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* ── Agent Aliases ─────────────────────────────────────────────── */}
      <div className={`${CARD} p-4 mt-6`}>
        <button
          className="flex items-center gap-2 text-sm font-medium text-gray-300 hover:text-amber-400 transition-colors w-full"
          onClick={() => setShowAliases(!showAliases)}
        >
          {showAliases ? <ChevronDown size={16}/> : <ChevronRight size={16}/>}
          <Users size={16}/>
          Agent Aliases
          <span className="text-xs text-gray-500 ml-2">Map alternate names on commission statements to agents</span>
        </button>

        {showAliases && (
          <div className="mt-4 space-y-4">
            {/* Add new alias */}
            <div className="flex gap-2 items-end flex-wrap">
              <div className="flex-1 min-w-[180px]">
                <label className="text-xs text-gray-500 mb-1 block">Name on statement</label>
                <input
                  className={INPUT}
                  placeholder="e.g. Aliya Martinez"
                  value={newAlias.aliasName}
                  onChange={e => setNewAlias(p => ({ ...p, aliasName: e.target.value }))}
                />
              </div>
              <div className="flex-1 min-w-[180px]">
                <label className="text-xs text-gray-500 mb-1 block">Maps to agent</label>
                <select
                  className={SELECT + ' w-full'}
                  value={newAlias.userId}
                  onChange={e => setNewAlias(p => ({ ...p, userId: e.target.value }))}
                >
                  <option value="">Select agent...</option>
                  {agencyMembers.map(m => (
                    <option key={m.user_id || m.id} value={m.user_id || m.id}>
                      {m.first_name} {m.last_name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex-1 min-w-[120px]">
                <label className="text-xs text-gray-500 mb-1 block">Notes (optional)</label>
                <input
                  className={INPUT}
                  placeholder="e.g. Spouse"
                  value={newAlias.notes}
                  onChange={e => setNewAlias(p => ({ ...p, notes: e.target.value }))}
                />
              </div>
              <button
                className={BTN_PRI}
                disabled={!newAlias.aliasName || !newAlias.userId}
                onClick={async () => {
                  const res = await fetch(`${BASE}/agency/commissions/aliases`, {
                    method: 'POST', credentials: 'include',
                    headers: { 'Content-Type': 'application/json', ...csrfHeadersFor('POST') },
                    body: JSON.stringify(newAlias),
                  });
                  const j = await res.json();
                  if (j.ok) {
                    setNewAlias({ aliasName: '', userId: '', notes: '' });
                    loadAliases();
                  } else {
                    alert(j.error || 'Failed to create alias');
                  }
                }}
              >
                <Plus size={14}/> Add
              </button>
            </div>

            {/* Existing aliases */}
            {aliases.length > 0 && (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-gray-500 text-xs border-b border-gray-800">
                    <th className="text-left py-2">Statement Name</th>
                    <th className="text-left py-2">Maps To</th>
                    <th className="text-left py-2">Notes</th>
                    <th className="py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {aliases.map(a => (
                    <tr key={a.id} className="border-b border-gray-800/50">
                      <td className="py-2 text-amber-400 font-medium">{a.alias_name}</td>
                      <td className="py-2 text-white">{a.first_name} {a.last_name}</td>
                      <td className="py-2 text-gray-400">{a.notes || '—'}</td>
                      <td className="py-2 text-right">
                        <button
                          className="text-gray-500 hover:text-red-400 transition-colors"
                          onClick={async () => {
                            const res = await fetch(`${BASE}/agency/commissions/aliases/${a.id}`, {
                              method: 'DELETE', credentials: 'include',
                              headers: csrfHeadersFor('DELETE'),
                            });
                            const j = await res.json();
                            if (j.ok) loadAliases();
                          }}
                        >
                          <Trash2 size={14}/>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {aliases.length === 0 && (
              <p className="text-gray-500 text-xs">No aliases configured. Add one to auto-match names that appear differently on carrier statements.</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// TAB: LEDGER
// ═════════════════════════════════════════════════════════════════════════════
function LedgerTab() {
  const [entries, setEntries] = useState([]);
  const [totals, setTotals] = useState({});
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ carrier: '', startDate: '', endDate: '', status: '', entryType: '' });
  const [showAddModal, setShowAddModal] = useState(false);
  const [agents, setAgents] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k === 'entryType' ? 'entryType' : k, v); });
      const [ledgerRes, membersRes] = await Promise.all([
        fetch(`${BASE}/agency/commissions/ledger?${params}`, { credentials: 'include' }),
        fetch(`${BASE}/api/agency-setup/team`, { credentials: 'include' }),
      ]);
      const [lj, mj] = await Promise.all([ledgerRes.json(), membersRes.json()]);
      if (lj.ok) { setEntries(lj.data || []); setTotals(lj.totals || {}); }
      if (mj.ok) setAgents(mj.data || []);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const exportCSV = () => {
    const header = ['Date','Agent','Carrier','Type','Plan','Amount','Status'];
    const rows = entries.map(e => [
      e.payment_date ? new Date(e.payment_date).toLocaleDateString() : '',
      e.agent_name || '',
      e.carrier_name || '',
      e.entry_type || '',
      e.plan_type || '',
      e.amount || 0,
      e.status || '',
    ]);
    const csv = [header, ...rows].map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'agency-ledger.csv'; a.click();
  };

  const setFilter = (k, v) => setFilters(f => ({ ...f, [k]: v }));

  return (
    <div className="space-y-5">
      {/* Totals */}
      <div className="grid grid-cols-3 gap-4">
        <div className={`${CARD} p-4`}>
          <p className="text-xs text-gray-500 mb-1">Total Received</p>
          <p className="text-xl font-bold text-emerald-400">{fmtCurrency(totals.received)}</p>
        </div>
        <div className={`${CARD} p-4`}>
          <p className="text-xs text-gray-500 mb-1">Total Expected</p>
          <p className="text-xl font-bold text-gray-300">{fmtCurrency(totals.expected)}</p>
        </div>
        <div className={`${CARD} p-4`}>
          <p className="text-xs text-gray-500 mb-1">Variance</p>
          <p className={`text-xl font-bold ${parseFloat(totals.variance) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
            {fmtCurrency(totals.variance)}
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className={`${CARD} p-4`}>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          <input placeholder="Carrier..." className={INPUT} value={filters.carrier} onChange={e => setFilter('carrier', e.target.value)} />
          <input type="date" className={INPUT} value={filters.startDate} onChange={e => setFilter('startDate', e.target.value)} />
          <input type="date" className={INPUT} value={filters.endDate} onChange={e => setFilter('endDate', e.target.value)} />
          <select className={SELECT} value={filters.status} onChange={e => setFilter('status', e.target.value)}>
            <option value="">All Statuses</option>
            <option value="received">Received</option>
            <option value="variance">Variance</option>
            <option value="missing">Missing</option>
            <option value="disputed">Disputed</option>
          </select>
          <select className={SELECT} value={filters.entryType} onChange={e => setFilter('entryType', e.target.value)}>
            <option value="">All Types</option>
            <option value="received">Received</option>
            <option value="expected">Expected</option>
            <option value="adjustment">Adjustment</option>
            <option value="chargeback">Chargeback</option>
          </select>
        </div>
        <div className="flex gap-2 mt-3">
          <button onClick={load} className={BTN_SM}><Filter size={12} />Apply</button>
          <button onClick={() => setFilters({ carrier:'', startDate:'', endDate:'', status:'', entryType:'' })} className={BTN_SM}><X size={12} />Clear</button>
          <button onClick={exportCSV} className={BTN_SM}><Download size={12} />Export CSV</button>
          <button onClick={() => setShowAddModal(true)} className={`${BTN_SM} ml-auto`}><Plus size={12} />Add Entry</button>
        </div>
      </div>

      {/* Table */}
      {loading ? <LoadingSpinner /> : !entries.length ? (
        <div className={`${CARD} p-12 text-center text-gray-600`}>
          <DollarSign size={40} className="mx-auto mb-3 opacity-30" />
          <p>No ledger entries found</p>
        </div>
      ) : (
        <div className={`${CARD} overflow-hidden`}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-800">
                  {['Date','Agent','Carrier','Type','Plan Type','Amount','Status'].map(h => (
                    <th key={h} className="text-left text-xs text-gray-500 px-4 py-3 font-medium">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-800/50">
                {entries.map(e => (
                  <tr key={e.id} className="hover:bg-gray-800/30 transition-colors">
                    <td className="px-4 py-3 text-gray-400 text-xs tabular-nums">{fmtDate(e.payment_date)}</td>
                    <td className="px-4 py-3 text-gray-200">{e.agent_name || '—'}</td>
                    <td className="px-4 py-3 text-gray-300">{e.carrier_name || '—'}</td>
                    <td className="px-4 py-3"><span className={statusBadge(e.entry_type)}>{e.entry_type}</span></td>
                    <td className="px-4 py-3 text-gray-400 text-xs">{e.plan_type || '—'}</td>
                    <td className="px-4 py-3 text-right tabular-nums font-medium text-gray-200">
                      {fmtCurrency(e.amount)}
                    </td>
                    <td className="px-4 py-3"><span className={statusBadge(e.status)}>{e.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showAddModal && (
        <AddLedgerEntryModal agents={agents} onClose={() => setShowAddModal(false)} onSaved={() => { setShowAddModal(false); load(); }} />
      )}
    </div>
  );
}

// Add Ledger Entry Modal
function AddLedgerEntryModal({ agents, onClose, onSaved }) {
  const [form, setForm] = useState({
    agentId: '', entryType: 'received', sourceType: 'manual',
    carrierName: '', commissionType: '', planType: '', amount: '',
    expectedAmount: '', effectiveDate: '', paymentDate: '', notes: '',
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${BASE}/agency/commissions/ledger`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...csrfHeadersFor('POST') },
        body: JSON.stringify(form),
      });
      const j = await res.json();
      if (j.ok) onSaved();
      else alert(j.error || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 border border-gray-800 rounded-xl p-6 w-full max-w-lg">
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-semibold text-white">Add Ledger Entry</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-300"><X size={18} /></button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <label className="text-xs text-gray-400 mb-1 block">Agent</label>
            <select className={SELECT} value={form.agentId} onChange={e => set('agentId', e.target.value)}>
              <option value="">— Select agent —</option>
              {agents.map(a => <option key={a.user_id || a.id} value={a.user_id || a.id}>{a.first_name} {a.last_name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-gray-400 mb-1 block">Entry Type</label>
            <select className={SELECT} value={form.entryType} onChange={e => set('entryType', e.target.value)}>
              <option value="received">Received</option>
              <option value="expected">Expected</option>
              <option value="adjustment">Adjustment</option>
              <option value="chargeback">Chargeback</option>
            </select>
          </div>
          <div>
            <label className="text-xs text-gray-400 mb-1 block">Carrier</label>
            <input className={INPUT} value={form.carrierName} onChange={e => set('carrierName', e.target.value)} placeholder="Carrier name" />
          </div>
          <div>
            <label className="text-xs text-gray-400 mb-1 block">Amount</label>
            <input className={INPUT} type="number" value={form.amount} onChange={e => set('amount', e.target.value)} placeholder="0.00" />
          </div>
          <div>
            <label className="text-xs text-gray-400 mb-1 block">Expected Amount</label>
            <input className={INPUT} type="number" value={form.expectedAmount} onChange={e => set('expectedAmount', e.target.value)} placeholder="0.00" />
          </div>
          <div>
            <label className="text-xs text-gray-400 mb-1 block">Payment Date</label>
            <input className={INPUT} type="date" value={form.paymentDate} onChange={e => set('paymentDate', e.target.value)} />
          </div>
          <div>
            <label className="text-xs text-gray-400 mb-1 block">Effective Date</label>
            <input className={INPUT} type="date" value={form.effectiveDate} onChange={e => set('effectiveDate', e.target.value)} />
          </div>
          <div className="col-span-2">
            <label className="text-xs text-gray-400 mb-1 block">Notes</label>
            <input className={INPUT} value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Optional notes" />
          </div>
        </div>
        <div className="flex gap-3 mt-5">
          <button onClick={save} disabled={saving} className={BTN_PRI}>
            {saving ? <RefreshCw size={14} className="animate-spin" /> : <Check size={14} />}
            Save Entry
          </button>
          <button onClick={onClose} className={BTN_SEC}><X size={14} />Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// TAB: AGENT PAYOUTS
// ═════════════════════════════════════════════════════════════════════════════
function PayoutsTab() {
  const [year, setYear] = useState(CUR_YEAR);
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pdfAgent, setPdfAgent] = useState(null);
  const [emailingAgent, setEmailingAgent] = useState(null);
  const [markingPaid, setMarkingPaid] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BASE}/agency/commissions/payouts/summary?year=${year}&month=${month}`, { credentials: 'include' });
      const j = await res.json();
      if (j.ok) setData(j.data || []);
    } finally {
      setLoading(false);
    }
  }, [year, month]);

  useEffect(() => { load(); }, [load]);

  const handleDownloadPDF = (agentId) => {
    window.open(`${BASE}/agency/commissions/payouts/${agentId}/pdf?year=${year}&month=${month}`, '_blank');
  };

  const handleEmail = async (agentId, agentName) => {
    if (!window.confirm(`Email commission statement to ${agentName}?`)) return;
    setEmailingAgent(agentId);
    try {
      const res = await fetch(`${BASE}/agency/commissions/payouts/${agentId}/email`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...csrfHeadersFor('POST') },
        body: JSON.stringify({ year, month }),
      });
      const j = await res.json();
      alert(j.ok ? j.message : (j.error || 'Email failed'));
      if (j.ok) load();
    } finally {
      setEmailingAgent(null);
    }
  };

  const handleMarkPaid = async (agent) => {
    setMarkingPaid(agent.agentId);
    try {
      const method = agent.payoutId ? 'PUT' : 'POST';
      const url = agent.payoutId
        ? `${BASE}/agency/commissions/payouts/${agent.payoutId}`
        : `${BASE}/agency/commissions/payouts`;
      const body = agent.payoutId
        ? { status: 'paid' }
        : { agentId: agent.agentId, periodYear: year, periodMonth: month, amount: agent.overrideEarned };

      await fetch(url, {
        method, credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      load();
    } finally {
      setMarkingPaid(null);
    }
  };

  return (
    <div className="space-y-5">
      {/* Period selector */}
      <div className="flex items-center gap-3">
        <select value={month} onChange={e => setMonth(parseInt(e.target.value))} className={SELECT}>
          {MONTHS.map((m, i) => <option key={i} value={i+1}>{m}</option>)}
        </select>
        <select value={year} onChange={e => setYear(parseInt(e.target.value))} className={SELECT}>
          {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
        </select>
        <button onClick={load} className={BTN_SM}><RefreshCw size={12} />Refresh</button>
        <p className="text-sm text-gray-500 ml-2">
          Showing commission data for {MONTHS[month-1]} {year}
        </p>
      </div>

      {loading ? <LoadingSpinner /> : !data.length ? (
        <div className={`${CARD} p-12 text-center text-gray-600`}>
          <Users size={40} className="mx-auto mb-3 opacity-30" />
          <p>No agents found</p>
        </div>
      ) : (
        <div className={`${CARD} overflow-hidden`}>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-800">
                {['Agent','Clients','FY','Renewal','Override Earned','Status','Actions'].map(h => (
                  <th key={h} className="text-left text-xs text-gray-500 px-4 py-3 font-medium">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800/50">
              {data.map(a => (
                <tr key={a.agentId} className="hover:bg-gray-800/30 transition-colors">
                  <td className="px-4 py-3">
                    <p className="text-sm font-medium text-gray-200">{a.agentName}</p>
                    <p className="text-xs text-gray-600">{a.agentEmail}</p>
                  </td>
                  <td className="px-4 py-3 text-gray-400 tabular-nums">{a.activeClients}</td>
                  <td className="px-4 py-3 text-gray-300 tabular-nums">{fmtCurrency(a.fyTotal)}</td>
                  <td className="px-4 py-3 text-gray-300 tabular-nums">{fmtCurrency(a.rnTotal)}</td>
                  <td className="px-4 py-3 font-semibold text-amber-400 tabular-nums">{fmtCurrency(a.overrideEarned)}</td>
                  <td className="px-4 py-3">
                    {a.payoutStatus
                      ? <span className={statusBadge(a.payoutStatus)}>{a.payoutStatus}</span>
                      : <span className="text-xs text-gray-600">—</span>
                    }
                    {a.statementSent && <span className="ml-1 text-xs text-emerald-400">✓ Sent</span>}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5">
                      <button
                        onClick={() => handleDownloadPDF(a.agentId)}
                        className={BTN_SM}
                        title="Download PDF statement"
                      >
                        <Download size={11} />PDF
                      </button>
                      <button
                        onClick={() => handleEmail(a.agentId, a.agentName)}
                        disabled={emailingAgent === a.agentId}
                        className={BTN_SM}
                        title="Email statement to agent"
                      >
                        {emailingAgent === a.agentId ? <RefreshCw size={11} className="animate-spin" /> : <Mail size={11} />}
                        Email
                      </button>
                      {a.payoutStatus !== 'paid' && (
                        <button
                          onClick={() => handleMarkPaid(a)}
                          disabled={markingPaid === a.agentId}
                          className={BTN_SM}
                        >
                          {markingPaid === a.agentId ? <RefreshCw size={11} className="animate-spin" /> : <Check size={11} />}
                          Paid
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// TAB: RECONCILIATION
// ═════════════════════════════════════════════════════════════════════════════
function ReconciliationTab() {
  const [year, setYear] = useState(CUR_YEAR);
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BASE}/agency/commissions/reconciliation?year=${year}&month=${month}`, { credentials: 'include' });
      const j = await res.json();
      if (j.ok) setData(j.data || []);
    } finally {
      setLoading(false);
    }
  }, [year, month]);

  useEffect(() => { load(); }, [load]);

  const totals = data.reduce((acc, r) => ({
    expected: acc.expected + r.expected,
    received: acc.received + r.received,
    variance: acc.variance + r.variance,
  }), { expected: 0, received: 0, variance: 0 });

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3">
        <select value={month} onChange={e => setMonth(parseInt(e.target.value))} className={SELECT}>
          {MONTHS.map((m, i) => <option key={i} value={i+1}>{m}</option>)}
        </select>
        <select value={year} onChange={e => setYear(parseInt(e.target.value))} className={SELECT}>
          {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
        </select>
        <button onClick={load} className={BTN_SM}><RefreshCw size={12} />Refresh</button>
      </div>

      {/* Summary row */}
      <div className="grid grid-cols-3 gap-4">
        <div className={`${CARD} p-4`}>
          <p className="text-xs text-gray-500 mb-1">Expected</p>
          <p className="text-xl font-bold text-gray-300">{fmtCurrency(totals.expected)}</p>
        </div>
        <div className={`${CARD} p-4`}>
          <p className="text-xs text-gray-500 mb-1">Received</p>
          <p className="text-xl font-bold text-emerald-400">{fmtCurrency(totals.received)}</p>
        </div>
        <div className={`${CARD} p-4`}>
          <p className="text-xs text-gray-500 mb-1">Variance</p>
          <p className={`text-xl font-bold ${totals.variance >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
            {totals.variance >= 0 ? '+' : ''}{fmtCurrency(totals.variance)}
          </p>
        </div>
      </div>

      {loading ? <LoadingSpinner /> : !data.length ? (
        <div className={`${CARD} p-12 text-center text-gray-600`}>
          <Check size={40} className="mx-auto mb-3 opacity-30" />
          <p>No carrier configs found. Configure commission structures in Agency Setup → Commissions.</p>
        </div>
      ) : (
        <div className={`${CARD} overflow-hidden`}>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-800">
                {['Carrier','Initial Clients','Renewal Clients','Config Rate (RN)','Expected','Received','Variance','Status'].map(h => (
                  <th key={h} className="text-left text-xs text-gray-500 px-4 py-3 font-medium">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800/50">
              {data.map((r, i) => (
                <tr key={i} className="hover:bg-gray-800/30 transition-colors">
                  <td className="px-4 py-3 text-gray-200 font-medium">{r.carrier}</td>
                  <td className="px-4 py-3 text-gray-400 tabular-nums">{r.initialClients}</td>
                  <td className="px-4 py-3 text-gray-400 tabular-nums">{r.renewalClients}</td>
                  <td className="px-4 py-3 text-gray-400 tabular-nums">{fmtCurrency(r.configRenewalRate)}</td>
                  <td className="px-4 py-3 text-gray-300 tabular-nums">{fmtCurrency(r.expected)}</td>
                  <td className="px-4 py-3 text-emerald-400 tabular-nums">{fmtCurrency(r.received)}</td>
                  <td className={`px-4 py-3 tabular-nums font-medium ${r.variance < 0 ? 'text-red-400' : 'text-emerald-400'}`}>
                    {r.variance >= 0 ? '+' : ''}{fmtCurrency(r.variance)}
                  </td>
                  <td className="px-4 py-3"><span className={statusBadge(r.status)}>{r.status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// TAB: ALERTS (stub)
// ═════════════════════════════════════════════════════════════════════════════
function AlertsTab() {
  return (
    <div className={`${CARD} p-16 text-center`}>
      <AlertCircle size={48} className="mx-auto mb-4 text-gray-700" />
      <h3 className="text-gray-400 font-medium mb-2">Alert Detection Coming Soon</h3>
      <p className="text-sm text-gray-600 max-w-sm mx-auto">
        Automatic detection of missing statements, payment variances, chargebacks, and at-risk override revenue will appear here.
      </p>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// TAB: SUMMARY (original view)
// ═════════════════════════════════════════════════════════════════════════════
function SummaryTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BASE}/agency/commissions/summary`, { credentials: 'include' });
      const j = await res.json();
      if (j.ok) setData(j);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <LoadingSpinner />;

  const maxAgent = data?.agents?.[0]?.totalCommissions || 1;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-3 gap-4">
        <KPICard label="Total Revenue (YTD)" value={fmtCurrency(data?.totalRevenue)} color="emerald" icon={DollarSign} />
        <KPICard label="Revenue at Risk" value={fmtCurrency(data?.revenueAtRisk)} color="red" icon={AlertTriangle} sub={`${data?.atRiskClientCount || 0} at-risk clients`} />
        <KPICard label="Agents Earning" value={`${data?.agents?.filter(a => a.totalCommissions > 0).length || 0} / ${data?.agents?.length || 0}`} color="blue" icon={Users} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className={`${CARD} lg:col-span-2 overflow-hidden`}>
          <div className="px-5 py-4 border-b border-gray-800">
            <h3 className="text-sm font-semibold text-gray-300 flex items-center gap-2"><BarChart3 size={16} />Per-Agent Breakdown</h3>
          </div>
          {!data?.agents?.length ? (
            <div className="p-12 text-center text-gray-600">No commission data</div>
          ) : (
            <div className="divide-y divide-gray-800/50">
              {data.agents.map((a, i) => (
                <div key={a.agentId} className="px-5 py-3">
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-gray-600 w-5 text-right">#{i+1}</span>
                      <div>
                        <p className="text-sm font-medium text-gray-200">{a.agentName}</p>
                        <p className="text-xs text-gray-600">{a.clientCount} clients</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-bold text-emerald-400">{fmtCurrency(a.totalCommissions)}</p>
                      <div className="flex gap-2 text-xs text-gray-600">
                        <span>FY: {fmtCurrency(a.firstYear)}</span>
                        <span>RN: {fmtCurrency(a.renewal)}</span>
                      </div>
                    </div>
                  </div>
                  <div className="w-full h-1 bg-gray-800 rounded-full overflow-hidden">
                    <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${(a.totalCommissions / maxAgent) * 100}%` }} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className={`${CARD} overflow-hidden`}>
          <div className="px-5 py-4 border-b border-gray-800">
            <h3 className="text-sm font-semibold text-gray-300 flex items-center gap-2"><PieChart size={16} />By Carrier</h3>
          </div>
          {!data?.byCarrier?.length ? (
            <div className="p-8 text-center text-gray-600 text-sm">No carrier data</div>
          ) : (
            <div className="divide-y divide-gray-800/50">
              {data.byCarrier.map(c => {
                const total = data.byCarrier.reduce((s, x) => s + x.total, 0) || 1;
                const pct = Math.round((c.total / total) * 100);
                return (
                  <div key={c.carrier} className="px-5 py-3">
                    <div className="flex justify-between mb-1">
                      <span className="text-sm text-gray-300">{c.carrier}</span>
                      <span className="text-sm font-bold text-gray-200">{fmtCurrency(c.total)}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-1 bg-gray-800 rounded-full overflow-hidden">
                        <div className="h-full bg-amber-400 rounded-full" style={{ width: `${pct}%` }} />
                      </div>
                      <span className="text-xs text-gray-600 w-7 text-right">{pct}%</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// SHARED UI COMPONENTS
// ═════════════════════════════════════════════════════════════════════════════
function KPICard({ label, value, color, icon: Icon, sub }) {
  const colorMap = {
    amber:   'text-amber-400 border-amber-400',
    emerald: 'text-emerald-400 border-emerald-400',
    red:     'text-red-400 border-red-400',
    blue:    'text-blue-400 border-blue-400',
  };
  return (
    <div className={`${CARD} p-5 border-t-2 ${colorMap[color]?.split(' ')[1] || 'border-gray-700'}`}>
      <div className="flex items-center gap-2 mb-2">
        {Icon && <Icon size={16} className={colorMap[color]?.split(' ')[0] || 'text-gray-400'} />}
        <p className="text-xs text-gray-500">{label}</p>
      </div>
      <p className={`text-2xl font-bold ${colorMap[color]?.split(' ')[0] || 'text-white'}`}>{value}</p>
      {sub && <p className="text-xs text-gray-600 mt-1">{sub}</p>}
    </div>
  );
}

function LoadingSpinner() {
  return (
    <div className="flex items-center justify-center py-16">
      <RefreshCw className="text-amber-400 animate-spin" size={24} />
    </div>
  );
}
